import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, Search, X } from "lucide-react";
import logo from "../img/logo.png";
import { scrollToElement } from "../utils/scrollUtils";
import { trackContactClick } from "../utils/analytics";

const CATEGORY_ITEMS = ["Sofa", "Ghế", "Giường"];

export default function Header({ search, setSearch, onSearchSubmit, onCategorySelect }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const solidHeader = scrolled || menuOpen || searchOpen;

  const closePanels = () => {
    setMenuOpen(false);
    setSearchOpen(false);
  };

  const handleScrollTo = (id) => {
    closePanels();
    scrollToElement(id, () => {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#${id}`
      );
    });
  };

  const handleCategory = (type) => {
    closePanels();
    onCategorySelect?.(type);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    onSearchSubmit?.();
    handleScrollTo("products");
  };

  const textColor = solidHeader ? "text-[var(--color-bg-dark)]" : "text-white";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solidHeader ? "border-b border-gray-100 bg-white/95 shadow-sm backdrop-blur" : "bg-transparent"
      }`}
    >
      <div className="relative z-50 mx-auto flex h-[68px] max-w-7xl items-center gap-4 bg-inherit px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => {
            closePanels();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex min-h-11 shrink-0 items-center gap-2 !bg-transparent !p-0 !shadow-none"
          aria-label="Về đầu trang"
        >
          <img src={logo} alt="Cưng Home" className="h-10 w-28 object-contain sm:w-32" />
          <span
            className={`hidden whitespace-nowrap font-['Playfair_Display'] text-lg font-bold sm:inline ${textColor}`}
          >
            Xưởng Sofa
          </span>
        </button>

        <nav className="ml-auto hidden items-center gap-5 lg:flex" aria-label="Điều hướng chính">
          <button
            type="button"
            onClick={() => handleCategory("Tất cả")}
            className={`!bg-transparent !p-0 text-sm font-semibold !shadow-none transition hover:!text-[var(--color-primary)] ${textColor}`}
          >
            Sản phẩm
          </button>
          {CATEGORY_ITEMS.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleCategory(type)}
              className={`!bg-transparent !p-0 text-sm font-semibold !shadow-none transition hover:!text-[var(--color-primary)] ${textColor}`}
            >
              {type}
            </button>
          ))}
          <button
            type="button"
            onClick={() => handleScrollTo("contact")}
            className={`!bg-transparent !p-0 text-sm font-semibold !shadow-none transition hover:!text-[var(--color-primary)] ${textColor}`}
          >
            Liên hệ
          </button>
        </nav>

        <form
          onSubmit={handleSearchSubmit}
          className={`ml-auto hidden h-10 w-56 items-center rounded-md border px-3 lg:flex xl:w-64 ${
            solidHeader
              ? "border-gray-200 bg-gray-50"
              : "border-white/35 bg-black/15 backdrop-blur-sm"
          }`}
        >
          <Search className={`h-4 w-4 shrink-0 ${solidHeader ? "text-gray-500" : "text-white/80"}`} />
          <input
            type="text"
            role="searchbox"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Tìm sofa, giường..."
            className={`h-full min-w-0 flex-1 bg-transparent px-2 text-sm outline-none placeholder:text-current placeholder:opacity-60 ${textColor}`}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Xóa tìm kiếm"
              className={`touch-target flex items-center justify-center !bg-transparent !p-0 !shadow-none ${textColor}`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </form>

        <a
          href="tel:0984842841"
          onClick={() => trackContactClick("Hotline desktop header")}
          className="hidden h-10 shrink-0 items-center gap-2 rounded-md bg-[var(--color-primary)] px-3 text-sm font-semibold !text-white shadow-sm transition hover:bg-[#805b42] xl:flex"
        >
          <Phone className="h-4 w-4" />
          0984 842 841
        </a>

        <div className="ml-auto flex items-center gap-1 lg:hidden">
          <button
            type="button"
            onClick={() => {
              setSearchOpen((open) => !open);
              setMenuOpen(false);
            }}
            className={`flex h-11 w-11 items-center justify-center rounded-full !p-0 !shadow-none ${
              solidHeader ? "!bg-gray-100 !text-[var(--color-primary)]" : "!bg-white/15 !text-white backdrop-blur"
            }`}
            aria-label={searchOpen ? "Đóng tìm kiếm" : "Mở tìm kiếm"}
          >
            {searchOpen ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
          </button>
          <button
            type="button"
            onClick={() => {
              setMenuOpen((open) => !open);
              setSearchOpen(false);
            }}
            className="flex h-11 w-11 items-center justify-center rounded-full !bg-[var(--color-primary)] !p-0 !text-white !shadow-sm"
            aria-label={menuOpen ? "Đóng menu" : "Mở menu"}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {searchOpen && (
          <motion.form
            onSubmit={handleSearchSubmit}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-gray-100 bg-white px-4 lg:hidden"
          >
            <div className="mx-auto flex max-w-2xl items-center gap-2 py-3">
              <div className="flex min-h-11 flex-1 items-center rounded-md border border-gray-200 bg-gray-50 px-3">
                <Search className="h-4 w-4 text-gray-500" />
                <input
                  ref={searchInputRef}
                  type="text"
                  role="searchbox"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Tìm theo tên sản phẩm, chất liệu..."
                  className="h-full min-w-0 flex-1 bg-transparent px-2 text-base text-gray-800 outline-none"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Xóa tìm kiếm"
                    className="touch-target -mr-3 flex items-center justify-center !bg-transparent !p-0 !text-gray-500 !shadow-none"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="min-h-11 shrink-0 rounded-md bg-[var(--color-primary)] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#805b42]"
              >
                Tìm
              </button>
            </div>
          </motion.form>
        )}

        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-gray-100 bg-white shadow-lg lg:hidden"
            aria-label="Menu di động"
          >
            <div className="mx-auto grid max-w-2xl grid-cols-2 gap-2 p-4">
              <button
                type="button"
                onClick={() => handleCategory("Tất cả")}
                className="rounded-md border border-gray-200 !bg-white px-3 py-3 text-left text-sm font-semibold !text-[var(--color-text)] !shadow-none"
              >
                Tất cả sản phẩm
              </button>
              {CATEGORY_ITEMS.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleCategory(type)}
                  className="rounded-md border border-gray-200 !bg-white px-3 py-3 text-left text-sm font-semibold !text-[var(--color-text)] !shadow-none"
                >
                  {type}
                </button>
              ))}
              <a
                href="tel:0984842841"
                onClick={() => trackContactClick("Hotline mobile menu")}
                className="flex items-center gap-2 rounded-md bg-[var(--color-primary)] px-3 py-3 text-sm font-semibold !text-white"
              >
                <Phone className="h-4 w-4" />
                Gọi tư vấn
              </a>
              <button
                type="button"
                onClick={() => handleScrollTo("contact")}
                className="rounded-md border border-gray-200 !bg-white px-3 py-3 text-left text-sm font-semibold !text-[var(--color-text)] !shadow-none"
              >
                Liên hệ
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
